import React, { useEffect, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import { debounce } from 'lodash';

import {
  Autocomplete,
  BackLink,
  Button,
  IconButton,
} from '../../components/atoms';
import {
  ArrowLeft as ArrowLeftIcon,
  ArrowRight as ArrowRightIcon,
} from '../../components/icons';
import { BottomBar, Hidden, Page } from '../../components/layouts';
import { AddressFormat } from '../../components/molecules';
import { PageHeader } from '../../components/partials/PageHeader';
import { analyticsService } from '../../core/services';
import { domUtils, urlUtils } from '../../utils';
import * as Api from '../../weborder/Api';
import {
  DispatchProps,
  StateProps,
} from '../../weborder/component/NewLocation';
import * as Data from '../../weborder/Data';
import { LoadMaps } from '../../weborder/MapLoader';

type Props = StateProps & DispatchProps;

const useStyles = makeStyles((theme: Theme) => ({
  actionField: {
    display: 'flex',
    flexFlow: 'row nowrap',

    '& .MuiAutocomplete-root': {
      width: '100%',
    },
    '& .MuiInputBase-adornedStart:not(.Mui-error)': {
      border: 'none',
    },
    '& .MuiInputLabel-formControl': {
      position: 'absolute',
    },
  },

  [theme.breakpoints.up('md')]: {
    actionField: {
      '& .MuiAutocomplete-root': {
        marginRight: theme.spacing(1),
        width: 'calc(100% - 160px)',
      },
      '& .MuiButtonBase-root': {
        width: theme.spacing(20),
      },
    },
  },
}));

export function DeliveryNewLocation(props: Props): JSX.Element {
  const classes = useStyles();
  const {
    AddLocation: addLocation,
    AppConfig: appConfig,
    Location: location,
    SaveAddressSuggestion: saveAddressSuggestion,
    translate,
  } = props;

  const [place, setPlace] = useState<google.maps.places.PlaceResult>(null);
  const [places, setPlaces] = useState<google.maps.places.PlacesService>(null);
  const [selected, setSelected] = useState<
    google.maps.places.AutocompletePrediction
  >(null);
  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  useEffect(() => {
    domUtils.skipToContent();
  }, []);

  useEffect(() => {
    if (appConfig.status === 'success') {
      LoadMaps(appConfig.data.GoogleMapsApiKeyWeb).then(() => {
        setPlaces(
          new google.maps.places.PlacesService(document.createElement('div')),
        );
      });
    }
  }, [appConfig.status]);

  useEffect(() => {
    const isLocationValid = selected !== null && place !== null;

    setIsDisabled(!isLocationValid);
  }, [selected, place]);

  const trackBackSelected = (): void => {
    const eventData = {
      amp_label: 'Back Selected',
      amp_view: 'Delivery New Location',
    };
    analyticsService.track(eventData);
  };

  const handleBackLink = (): void => {
    trackBackSelected();
    urlUtils.navigateToHash('#/delivery/');
  };

  const handleInputChange = debounce((input) => {
    const inputValue = input.target.value;
    const minGeocodeInputLength = 5;

    if (inputValue.length < minGeocodeInputLength) {
      setIsDisabled(true);
      return;
    }

    if (appConfig.status === 'success') {
      const { data } = appConfig;

      LoadMaps(data.GoogleMapsApiKeyWeb).then((tools) => {
        const bounds = {
          // North East : bigger
          // South West : smaller
          north: data.MapBoundryMaxLatitude,
          south: data.MapBoundryMinLatitude,
          east: data.MapBoundryMaxLongitude,
          west: data.MapBoundryMinLongitude,
        };

        tools.AutoComplete.getPlacePredictions(
          {
            input: inputValue,
            location: Data.LatLng(location),
            radius: 200,
            bounds,
          },
          handleAutocompleteResponse,
        );
      });
    }
  }, 750);

  const handleAutocompleteResponse = (
    newSuggestions: google.maps.places.AutocompletePrediction[],
    status: google.maps.places.PlacesServiceStatus,
  ): void => {
    switch (status) {
      case google.maps.places.PlacesServiceStatus.OK:
        break;

      default:
        // const dataLog: any = {};

        // if (appConfig.status === 'success') {
        //   dataLog.AppNameId = appConfig.data.AppNameId;
        //   dataLog.WhiteLabelId = appConfig.data.WhiteLabelId;
        // }

        // Raven.captureMessage(
        //   'Error getting Google Maps Places predictions',
        //   {
        //     tags: { category: 'Third party library failure' },
        //     extra: {
        //       input: input,
        //       status: status,
        //       appconfig: dataLog,
        //     },
        //   },
        // );
        return;
    }

    if (newSuggestions) {
      newSuggestions.push({
        description: translate('My_address_is_not_listed'),
        place_id: 'customAddressRequired',
      } as google.maps.places.AutocompletePrediction);

      setSuggestions(newSuggestions);
    }
  };

  const handleConfirmAddress = (): void => {
    if (!place) {
      return;
    }

    if (!selected) {
      // const appconfig: any = {};

      // if (appConfig.status === 'success') {
      //   appconfig.AppNameId = appConfig.data.AppNameId;
      //   appconfig.WhiteLabelId = appConfig.data.WhiteLabelId;
      // }

      // Raven.captureMessage('Selected address is null', {
      //   tags: { category: 'Frontend error' },
      //   extra: {
      //     appconfig: appconfig,
      //   },
      // });
      return;
    }

    const addressComponents = place.address_components;
    let postalCode;
    addressComponents.forEach((addressComponent) => {
      if (addressComponent.types[0] === 'postal_code') {
        postalCode = addressComponent.long_name;
      }
    });

    saveAddressSuggestion(selected.description, postalCode);

    let confirmUrl = `#/delivery/confirm/${location.Latitude}/${location.Longitude}`;
    if (selected?.place_id) {
      confirmUrl += `/${selected.place_id}`;
    }
    urlUtils.navigateToHash(confirmUrl);
  };

  const handlePlaceSelected = (
    _event: React.ChangeEvent<HTMLInputElement>,
    selectedLocation: google.maps.places.AutocompletePrediction,
  ): void => {
    const placeId = selectedLocation?.place_id;

    if (!suggestions || suggestions.length === 0 || !places) {
      return;
    }

    if (placeId === 'customAddressRequired') {
      urlUtils.navigateToHash('#/delivery/map');
    }

    if (placeId) {
      const request: google.maps.places.PlaceDetailsRequest = {
        placeId,
        fields: ['geometry', 'address_component'],
      };

      places.getDetails(request, (result, status) => {
        switch (status) {
          case google.maps.places.PlacesServiceStatus.OK:
            break;

          default:
            // const dataLog: any = {};

            // if (appConfig.status === 'success') {
            //   dataLog.AppNameId = appConfig.data.AppNameId;
            //   dataLog.WhiteLabelId = appConfig.data.WhiteLabelId;
            // }

            // Raven.captureMessage('Error calling Google Maps Place Details', {
            //   tags: { category: 'Third party library failure' },
            //   extra: {
            //     result: result,
            //     status: status,
            //     locations: suggestions,
            //     appconfig: dataLog,
            //   },
            // });
            return;
        }

        addLocation(
          Data.ToGeo(result.geometry.location),
          Api.LocationCertainty.User,
        );
        setPlace(result);
      });
    }

    setSelected(selectedLocation);
  };

  const mobileBackButton = (
    <IconButton
      className="backButton"
      color="secondary"
      label={translate('Back')}
      onClick={handleBackLink}
    >
      <ArrowLeftIcon />
    </IconButton>
  );

  return (
    <Page pageName="delivery-new-location">
      <Hidden smDown>
        <PageHeader
          showProfileMenu={false}
          title={translate('Your_delivery_address')}
        >
          <BackLink
            label={translate('Back')}
            linkPath="/delivery"
            onClick={trackBackSelected}
          />
        </PageHeader>
      </Hidden>

      <article className="fd-content__main">
        <div className={classes.actionField}>
          <Autocomplete
            filterOptions={(
              options,
            ): google.maps.places.AutocompletePrediction[] => options}
            getOptionLabel={(option): string =>
              typeof option === 'string' ? option : option.description
            }
            onChange={handlePlaceSelected}
            onInputChange={handleInputChange}
            options={suggestions}
            placeholder={translate('Enter_your_address_here')}
            renderOption={(option): JSX.Element => (
              <AddressFormat option={option} />
            )}
            startIcon={mobileBackButton}
          />
          <Hidden smDown>
            <Button disabled={isDisabled} onClick={handleConfirmAddress}>
              {translate('Next')}
            </Button>
          </Hidden>
        </div>

        <Hidden mdUp>
          <BottomBar fixedOnly={['xs', 'sm']}>
            <Button
              disabled={isDisabled}
              endIcon={<ArrowRightIcon />}
              fullWidth
              onClick={handleConfirmAddress}
            >
              {translate('Next')}
            </Button>
          </BottomBar>
        </Hidden>
      </article>
    </Page>
  );
}
